import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { LogOut, ArrowLeft, LogIn } from 'lucide-react';
import CountHelpDrawer, { HelpButton } from './CountHelpDrawer';

export default function CountLayout({ children }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Detect Count PWA mode (installed from count-manifest.json)
  const isCountPWA = typeof window !== 'undefined' && localStorage.getItem('az_count_pwa') === '1';

  // Determine session ID from URL for context-aware "Return to Session"
  const sessionIdFromUrl = pathname.match(/\/count\/sessions\/(\d+)/)?.[1];

  // Is on the home page (no back button needed)
  const isHome = pathname === '/count' || pathname === '/count/' || pathname === '/count/sessions';

  // Set document title + theme color + manifest for Count mode PWA
  useEffect(() => {
    document.title = 'AbbeyZone Count';
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.content = '#0e7490';
    // Swap manifest to count-manifest.json for proper PWA install
    const manifestLink = document.getElementById('app-manifest');
    if (manifestLink) manifestLink.href = '/count-manifest.json';
    const touchIcon = document.getElementById('app-touch-icon');
    if (touchIcon) touchIcon.href = '/count-icon-192x192.png';
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) appleTitleMeta.content = 'AZ Count';
    // Strip ?app=count marker if still present
    const params = new URLSearchParams(window.location.search);
    if (params.has('app')) {
      params.delete('app');
      const clean = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (clean ? `?${clean}` : ''));
    }
    return () => {
      document.title = 'AbbeyZone | Abbey 5 Star Ltd';
      if (themeMeta) themeMeta.content = '#0f172a';
      if (manifestLink) manifestLink.href = '/manifest.json';
      if (touchIcon) touchIcon.href = '/logo.png';
      if (appleTitleMeta) appleTitleMeta.content = 'AbbeyZone';
    };
  }, []);

  // Session expiry — show re-auth overlay instead of silent redirect
  useEffect(() => {
    const handleExpired = () => setSessionExpired(true);
    window.addEventListener('auth:session-expired', handleExpired);
    return () => window.removeEventListener('auth:session-expired', handleExpired);
  }, []);

  // SW update available — show reload banner (only when user is on active count page)
  useEffect(() => {
    const handleSwUpdated = () => setUpdateAvailable(true);
    window.addEventListener('sw-updated', handleSwUpdated);
    return () => window.removeEventListener('sw-updated', handleSwUpdated);
  }, []);

  // Periodic health check — detect stale app state on Android tablets
  // When Android kills the webview process, the app may restart with stale cache
  useEffect(() => {
    const checkHealth = () => {
      // If we're online but the SW is unresponsive, attempt recovery
      if (!navigator.serviceWorker?.controller) return;
      const channel = new MessageChannel();
      const timeout = setTimeout(() => {
        // SW didn't respond in 3 seconds — it may be dead
        console.warn('[Health] SW unresponsive — attempting update');
        navigator.serviceWorker.ready.then(reg => reg.update()).catch(() => {});
      }, 3000);
      channel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        if (event.data?.status === 'alive') {
          // SW is healthy
        }
      };
      navigator.serviceWorker.controller.postMessage({ type: 'HEALTH_CHECK' }, [channel.port2]);
    };

    // Check on visibility change (tablet waking from sleep)
    const handleVisibility = () => {
      if (!document.hidden) checkHealth();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Also check periodically every 5 minutes
    const interval = setInterval(checkHealth, 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleReturnToFullApp = () => {
    // Count is ring-fenced — inform user to use AbbeyZone main app separately
    if (window.confirm('AbbeyZone Count is a focused counting environment.\n\nTo access the full AbbeyZone application, open it separately from your browser or home screen.\n\nDo you want to log out of Count?')) {
      handleLogout();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="count-layout">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {!isHome && (
            <button
              onClick={() => navigate('/count')}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
              data-testid="count-back-home"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <Link
            to="/count"
            className="text-lg font-semibold tracking-tight text-slate-900"
            data-testid="count-logo"
          >
            AbbeyZone Count
          </Link>
          <Badge variant="outline" className="text-[8px] px-1 py-0 border-cyan-300 text-cyan-700 bg-cyan-50 ml-1">COUNTING</Badge>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 hidden sm:inline" data-testid="count-user-name">
            {user?.first_name}
          </span>
          <HelpButton onClick={() => setHelpOpen(true)} />
          {!isCountPWA && (
            <button
              onClick={handleReturnToFullApp}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              title="Exit AbbeyZone Count"
              data-testid="count-back-to-full-app"
            >
              <span className="hidden sm:inline text-xs">Exit Count</span>
            </button>
          )}
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            title="Logout"
            data-testid="count-logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>

      {/* App update banner */}
      {updateAvailable && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-cyan-700 text-white px-4 py-2 flex items-center justify-between text-sm" data-testid="count-update-banner">
          <span>App update available</span>
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs"
            onClick={() => window.location.reload()}
            data-testid="count-update-reload-btn"
          >
            Reload Now
          </Button>
        </div>
      )}

      <Toaster />
      <SonnerToaster position="top-center" richColors />
      <CountHelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Session Expiry Recovery Overlay — covers ALL count pages */}
      {sessionExpired && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="count-session-expired-overlay">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Session Expired</h2>
            <p className="text-sm text-slate-500 mb-1">
              Your session has timed out, but your count data is safely stored on this device.
            </p>
            <p className="text-xs text-slate-400 mb-6">
              Log in again to continue counting or sync your data.
            </p>
            <Button
              onClick={() => {
                window.location.href = `/login?redirect=${encodeURIComponent(pathname)}`;
              }}
              className="w-full"
              data-testid="count-reauth-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log In to Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
